import { Box, Button, Group, SimpleGrid, Stack, Text, Title, Badge, Anchor } from "@mantine/core";
import { Link, useParams } from "react-router-dom";
import { getRelease, releases, isComingSoon, isReleased } from "@/content/releases";
import { getLive } from "@/content/live";
import { MediaImage } from "@/components/common/MediaImage";
import { AudioPlayer } from "@/components/audio/AudioPlayer";
import { StreamPlayer } from "@/components/video/StreamPlayer";
import { SpotifyEmbed } from "@/components/release/SpotifyEmbed";
import { ReleaseCard } from "@/components/release/ReleaseCard";
import { ReleaseBackdrop } from "@/components/release/ReleaseBackdrop";
import { LiveTile } from "@/components/live/LiveTile";
import { EmptyState } from "@/components/common/EmptyState";
import { PageContainer } from "@/components/layout/PageContainer";
import { formatDate, formatDuration } from "@/lib/format";

const STATUS_BADGE: Record<"announced" | "pre-release" | "released", { label: string; color: string }> = {
  announced: { label: "Upcoming", color: "cyan" },
  "pre-release": { label: "Pre-release", color: "magenta" },
  released: { label: "Released", color: "gray" },
};

export default function ReleasePage() {
  const { slug } = useParams<{ slug: string }>();
  const release = slug ? getRelease(slug) : undefined;

  if (!release) {
    return (
      <PageContainer>
        <EmptyState title="Release not found" body={`No release at "${slug}".`} />
      </PageContainer>
    );
  }

  // Navigation spreads at the bottom of the page: mirror the home-page
  // Coming-soon + Catalog layout so a visitor who landed deep on a release
  // (from a share/DSP link) gets the same discovery surface without bouncing
  // through the index. Exclude the release they're already on.
  const others = releases.filter((r) => r.slug !== release.slug);
  const comingSoonNav = others.filter(isComingSoon);
  const catalogNav = others.filter(isReleased).slice(0, 6);

  const liveClips =
    release.liveSlugs?.map((s) => getLive(s)).filter((l): l is NonNullable<typeof l> => Boolean(l)) ?? [];
  const isPreRelease = release.status === "pre-release";
  const isAnnounced = release.status === "announced";
  const statusBadge = STATUS_BADGE[release.status];

  // Listen block shows when there's any publicly-renderable audio. Released
  // catalog entries without a preview just don't render a Listen block —
  // visitors go to the DSP links instead.
  const hasAnyPublicPreview = release.tracks.some((t) => {
    if (!t.preview) return false;
    if (t.preview.ref.kind === "external") return Boolean(t.preview.ref.url);
    return true;
  });
  const hasDsps = (release.dsps?.length ?? 0) > 0;

  return (
    <Box style={{ position: "relative" }}>
      {release.background && <ReleaseBackdrop image={release.background} />}
      <PageContainer>
        <Stack gap="xl" style={{ position: "relative", zIndex: 1 }}>
        <Group align="flex-start" wrap="wrap" gap="xl">
        <Box style={{ flex: "0 0 320px", maxWidth: "100%" }}>
          <MediaImage
            image={release.cover}
            loading="eager"
            style={{ width: "100%", height: "auto", borderRadius: 12 }}
            sizes="(min-width: 768px) 320px, 100vw"
          />
        </Box>
        <Stack gap="sm" style={{ flex: "1 1 320px", minWidth: 0 }}>
          <Badge color={statusBadge.color} variant="light">
            {statusBadge.label}
          </Badge>
          <Title order={1} className="iridescent-text">
            {release.title}
          </Title>
          <Text c="dimmed">
            {release.artistCredit} · {release.label}
            {release.catalogNumber && ` · ${release.catalogNumber}`}
          </Text>
          {isPreRelease && release.releaseDate && (
            <Text size="sm" c="var(--accent-magenta)">
              Coming {formatDate(release.releaseDate)}
            </Text>
          )}
          {isAnnounced && (
            <Text size="sm" c="dimmed">
              Release date TBA
            </Text>
          )}
          {hasDsps && release.dsps && (
            <Group mt="sm">
              {release.dsps.map((d) => (
                <Button
                  key={d.name}
                  component="a"
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="light"
                  color="indigo"
                >
                  {d.name}
                </Button>
              ))}
            </Group>
          )}
          {isPreRelease && (
            <Group mt="sm">
              <Button component={Link} to="/access" color="indigo">
                Full listen (label / press)
              </Button>
            </Group>
          )}
        </Stack>
      </Group>

      {/* Listen block.
          - Released w/ Spotify embed URL: Spotify iframe (Spotify hosts audio).
          - Released w/o embed but with DSP links: links already shown above.
          - Released w/o anything: silent.
          - Pre-release: "preview coming soon" + press CTA above.
          - Announced (TBA): "teaser coming soon".
          - Has a public preview AudioRef: native player (rare for back catalog). */}
      {release.spotifyEmbedUrl ? (
        <Box>
          <Title order={2} size="h3" mb="sm">
            Listen
          </Title>
          <SpotifyEmbed url={release.spotifyEmbedUrl} title={`${release.title} on Spotify`} />
        </Box>
      ) : hasAnyPublicPreview ? (
        <Box>
          <Title order={2} size="h3" mb="sm">
            Listen
          </Title>
          <Stack gap="md">
            {release.tracks.map((t) => (
              <Box
                key={t.position}
                p="md"
                style={{
                  background: "var(--color-deep)",
                  border: "1px solid var(--color-ocean)",
                  borderRadius: 8,
                }}
              >
                <Group justify="space-between" mb="xs">
                  <Text fw={600}>
                    {t.position}. {t.title}
                  </Text>
                  {t.durationSec > 0 && (
                    <Text size="xs" c="dimmed">
                      {formatDuration(t.durationSec)}
                    </Text>
                  )}
                </Group>
                {t.preview ? (
                  <AudioPlayer audio={t.preview} label="Preview" />
                ) : (
                  <Text size="xs" c="dimmed">
                    Preview coming soon.
                  </Text>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      ) : isPreRelease ? (
        <Box
          p="md"
          style={{
            background: "var(--color-deep)",
            border: "1px dashed var(--color-ocean)",
            borderRadius: 8,
          }}
        >
          <Text size="sm" c="dimmed" ta="center">
            Public preview clip coming soon. Full listen via press access above.
          </Text>
        </Box>
      ) : isAnnounced ? (
        <Box
          p="md"
          style={{
            background: "var(--color-deep)",
            border: "1px dashed var(--color-ocean)",
            borderRadius: 8,
          }}
        >
          <Text size="sm" c="dimmed" ta="center">
            Teaser coming soon.
          </Text>
        </Box>
      ) : null}

      {/* Live clips featuring this track — useful as a tangible CTA for
          unreleased songs that don't have public preview audio yet. */}
      {liveClips.length > 0 && (
        <Box>
          <Title order={2} size="h3" mb="sm">
            Watch live
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {liveClips.map((show) => (
              <LiveTile key={show.slug} show={show} />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* Promo video */}
      {release.videos && release.videos.length > 0 && release.videos[0] && (
        <Box>
          <Title order={2} size="h3" mb="sm">
            Visualizer
          </Title>
          <StreamPlayer video={release.videos[0]} />
        </Box>
      )}

      {/* Description */}
      {release.description && (
        <Box maw={760}>
          <Title order={2} size="h3" mb="sm">
            About this release
          </Title>
          <Text c="var(--color-foam)">{release.description}</Text>
          {release.artistQuote && (
            <Box
              mt="lg"
              p="md"
              style={{
                borderLeft: "3px solid var(--accent-magenta)",
                background: "var(--color-deep)",
                borderRadius: 4,
              }}
            >
              <Text size="md" fs="italic">
                {release.artistQuote}
              </Text>
            </Box>
          )}
        </Box>
      )}

      {/* Press for this release */}
      {release.pressLinks && release.pressLinks.length > 0 && (
        <Box>
          <Title order={2} size="h3" mb="sm">
            Press
          </Title>
          <Stack gap="xs">
            {release.pressLinks.map((p) => (
              <Anchor key={p.url} href={p.url} target="_blank" rel="noopener noreferrer">
                {p.outlet} — {p.title}
              </Anchor>
            ))}
          </Stack>
        </Box>
      )}

        {/* Browse-more nav — Coming soon + Catalog spreads, same shape as
            the home page so visitors who land on a release page directly get
            a usable discovery surface. */}
        {comingSoonNav.length > 0 && (
          <Box>
            <Group justify="space-between" mb="sm">
              <Title order={2} size="h3">
                coming soon
              </Title>
              <Anchor component={Link} to="/releases">
                All upcoming →
              </Anchor>
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing="md">
              {comingSoonNav.map((r) => (
                <ReleaseCard key={r.slug} release={r} />
              ))}
            </SimpleGrid>
          </Box>
        )}
        {catalogNav.length > 0 && (
          <Box>
            <Group justify="space-between" mb="sm">
              <Title order={2} size="h3">
                catalog
              </Title>
              <Anchor component={Link} to="/releases">
                Full discography →
              </Anchor>
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing="md">
              {catalogNav.map((r) => (
                <ReleaseCard key={r.slug} release={r} />
              ))}
            </SimpleGrid>
          </Box>
        )}
        </Stack>
      </PageContainer>
    </Box>
  );
}
